/**
 * KPIs por músculo y por semana (§5.2): series efectivas, volumen en ventanas móviles,
 * frecuencia, balance empuje/tirón y superior/inferior, mapa de calor.
 *
 * Igual que en fuerza.ts, las series omitidas se excluyen (§4.3).
 */

import { addDias, claveSemana, diffDias, enVentana, ventana } from '../dates.ts';
import { GRUPOS, cargaMuscular, grupoDe, regionDe } from '../musculos.ts';
import type { Fecha, GrupoMuscular, Musculo, Patron, Region, SerieContexto, Unidad } from '../types.ts';
import { seriesEfectivas, volumenSerie } from './fuerza.ts';

// ---------------------------------------------------------------------------
// Series efectivas y volumen por músculo
// ---------------------------------------------------------------------------

/**
 * Series efectivas por músculo. Una serie de press de banca aporta 1 a pecho y 0.5 a
 * tríceps: contar 1 a cada uno inflaría el volumen de tríceps hasta hacerlo indistinguible
 * de un día de brazo.
 */
export function seriesPorMusculo(ctxs: SerieContexto[]): Map<Musculo, number> {
  const out = new Map<Musculo, number>();
  for (const c of seriesEfectivas(ctxs)) {
    for (const [m, peso] of cargaMuscular(c.ejercicio)) {
      out.set(m, (out.get(m) ?? 0) + peso);
    }
  }
  return out;
}

export function seriesPorGrupo(ctxs: SerieContexto[]): Map<GrupoMuscular, number> {
  const out = new Map<GrupoMuscular, number>();
  for (const [m, v] of seriesPorMusculo(ctxs)) {
    const g = grupoDe(m);
    if (!g) continue;
    out.set(g, (out.get(g) ?? 0) + v);
  }
  return out;
}

export function volumenPorMusculo(ctxs: SerieContexto[], unidad: Unidad = 'kg'): Map<Musculo, number> {
  const out = new Map<Musculo, number>();
  for (const c of seriesEfectivas(ctxs)) {
    const vol = volumenSerie(c.serie, unidad);
    for (const [m, peso] of cargaMuscular(c.ejercicio)) {
      out.set(m, (out.get(m) ?? 0) + vol * peso);
    }
  }
  return out;
}

export function volumenPorGrupo(ctxs: SerieContexto[], unidad: Unidad = 'kg'): Map<GrupoMuscular, number> {
  const out = new Map<GrupoMuscular, number>();
  for (const [m, v] of volumenPorMusculo(ctxs, unidad)) {
    const g = grupoDe(m);
    if (!g) continue;
    out.set(g, (out.get(g) ?? 0) + v);
  }
  return out;
}

/** Volumen por músculo en ventanas de 7 / 14 / 30 días (§5.2). */
export function volumenEnVentanas(
  ctxs: SerieContexto[],
  hasta: Fecha,
  unidad: Unidad = 'kg',
  dias: number[] = [7, 14, 30],
): Map<number, Map<Musculo, number>> {
  const out = new Map<number, Map<Musculo, number>>();
  for (const d of dias) {
    const v = ventana(hasta, d);
    out.set(d, volumenPorMusculo(ctxs.filter((c) => enVentana(c.fecha, v)), unidad));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Frecuencia (§5.2)
// ---------------------------------------------------------------------------

/**
 * Cuántas veces por semana se estimula cada músculo. Se cuentan **días distintos**, no
 * series: hacer cuatro ejercicios de pecho el mismo día es una frecuencia de 1, no de 4.
 */
export function frecuenciaPorMusculo(
  ctxs: SerieContexto[],
  hasta: Fecha,
  dias = 28,
): Map<Musculo, number> {
  const v = ventana(hasta, dias);
  const enRango = seriesEfectivas(ctxs).filter((c) => enVentana(c.fecha, v));
  const diasPorMusculo = new Map<Musculo, Set<Fecha>>();

  for (const c of enRango) {
    for (const [m] of cargaMuscular(c.ejercicio)) {
      const set = diasPorMusculo.get(m) ?? new Set<Fecha>();
      set.add(c.fecha);
      diasPorMusculo.set(m, set);
    }
  }

  const semanas = dias / 7;
  const out = new Map<Musculo, number>();
  for (const [m, set] of diasPorMusculo) out.set(m, set.size / semanas);
  return out;
}

/** Series efectivas por grupo y por semana, para graficar la evolución. */
export function seriesSemanalesPorGrupo(ctxs: SerieContexto[]): Map<string, Map<GrupoMuscular, number>> {
  const out = new Map<string, Map<GrupoMuscular, number>>();
  for (const c of seriesEfectivas(ctxs)) {
    const k = claveSemana(c.fecha);
    const semana = out.get(k) ?? new Map<GrupoMuscular, number>();
    for (const [m, peso] of cargaMuscular(c.ejercicio)) {
      const g = grupoDe(m);
      if (!g) continue;
      semana.set(g, (semana.get(g) ?? 0) + peso);
    }
    out.set(k, semana);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Balance (§5.2)
// ---------------------------------------------------------------------------

export interface Balance {
  a: number;
  b: number;
  /** a / (a+b), entre 0 y 1. 0.5 es equilibrio perfecto. null si no hay datos. */
  proporcion: number | null;
  /** true cuando la proporción se aparta del equilibrio más que `tolerancia`. */
  desbalanceado: boolean;
  detalle: string;
}

const TOLERANCIA_BALANCE = 0.15;

function armarBalance(
  a: number,
  b: number,
  etiquetaA: string,
  etiquetaB: string,
  tolerancia = TOLERANCIA_BALANCE,
): Balance {
  const total = a + b;
  if (total <= 0) {
    return { a, b, proporcion: null, desbalanceado: false, detalle: 'Sin datos en la ventana.' };
  }
  const proporcion = a / total;
  const desbalanceado = Math.abs(proporcion - 0.5) > tolerancia;
  const dominante = proporcion > 0.5 ? etiquetaA : etiquetaB;
  const menor = proporcion > 0.5 ? etiquetaB : etiquetaA;
  return {
    a,
    b,
    proporcion,
    desbalanceado,
    detalle: desbalanceado
      ? `${Math.round(Math.max(proporcion, 1 - proporcion) * 100)} % del trabajo es ${dominante}: falta ${menor}.`
      : 'Equilibrado.',
  };
}

/** §5.2 — el desequilibrio clásico: mucho pecho y brazo, poca espalda. */
export function balanceEmpujeTiron(ctxs: SerieContexto[]): Balance {
  let empuje = 0;
  let tiron = 0;
  for (const c of seriesEfectivas(ctxs)) {
    const p: Patron = c.ejercicio.patron;
    if (p === 'empuje') empuje += 1;
    else if (p === 'tiron') tiron += 1;
  }
  return armarBalance(empuje, tiron, 'empuje', 'tirón');
}

/** §5.2 — mucho superior, poca pierna. El core no entra en la comparación. */
export function balanceSuperiorInferior(ctxs: SerieContexto[]): Balance {
  let superior = 0;
  let inferior = 0;
  for (const c of seriesEfectivas(ctxs)) {
    const r: Region | null = regionDe(c.ejercicio.musculo_primario);
    if (r === 'superior') superior += 1;
    else if (r === 'inferior') inferior += 1;
  }
  return armarBalance(superior, inferior, 'superior', 'pierna');
}

// ---------------------------------------------------------------------------
// Mapa de calor y músculos abandonados (§5.2, §7)
// ---------------------------------------------------------------------------

export interface CeldaMapaCalor {
  grupo: GrupoMuscular;
  /** Series efectivas: los secundarios cuentan a mitad. Es la intensidad del color. */
  seriesEfectivas: number;
  /**
   * Series donde el grupo era el objetivo del ejercicio, no un acompañante. Es lo que
   * determina si el grupo se está entrenando de verdad.
   */
  seriesPrimarias: number;
  volumenKg: number;
  /** Objetivo configurable de series semanales; null si no se configuró. */
  objetivo: number | null;
  /** seriesEfectivas / objetivo, para colorear. null sin objetivo. */
  cumplimiento: number | null;
  /** Última fecha con estímulo PRIMARIO. Ver la nota de `abandonado`. */
  ultimoEstimulo: Fecha | null;
  diasSinEstimulo: number | null;
  abandonado: boolean;
}

/** §7 — grupo sin estímulo en 14 días. */
export const DIAS_ABANDONO = 14;

/**
 * Carga mínima para considerar que un músculo recibió estímulo real. 1 = sólo primario.
 *
 * No es un detalle: con el umbral en 0.5 (aceptando secundarios), un back extension —
 * lumbar primario, glúteos e isquios secundarios — hace que "pierna" figure entrenada sin
 * haber hecho un solo ejercicio de pierna, y la alerta de músculo abandonado de §7 nunca se
 * dispara. §5.2 pide detectar "qué se está abandonando": trabajo colateral no es
 * entrenamiento.
 */
export const UMBRAL_ESTIMULO = 1;

export function mapaCalor(
  ctxs: SerieContexto[],
  hasta: Fecha,
  opciones: {
    dias?: number;
    objetivoSeriesSemanales?: Partial<Record<GrupoMuscular, number>>;
    diasAbandono?: number;
    umbralEstimulo?: number;
  } = {},
): CeldaMapaCalor[] {
  const {
    dias = 7,
    objetivoSeriesSemanales = {},
    diasAbandono = DIAS_ABANDONO,
    umbralEstimulo = UMBRAL_ESTIMULO,
  } = opciones;

  const v = ventana(hasta, dias);
  const enRango = ctxs.filter((c) => enVentana(c.fecha, v));

  const series = seriesPorGrupo(enRango);
  const volumen = volumenPorGrupo(enRango, 'kg');

  const primariasEnRango = new Map<GrupoMuscular, number>();
  for (const c of seriesEfectivas(enRango)) {
    const g = grupoDe(c.ejercicio.musculo_primario);
    if (!g) continue;
    primariasEnRango.set(g, (primariasEnRango.get(g) ?? 0) + 1);
  }

  // El último estímulo se busca en todo el historial, no sólo en la ventana: justamente
  // interesa el grupo que NO aparece en la ventana.
  const ultimoPorGrupo = new Map<GrupoMuscular, Fecha>();
  for (const c of seriesEfectivas(ctxs)) {
    if (c.fecha > hasta) continue;
    for (const [m, carga] of cargaMuscular(c.ejercicio)) {
      if (carga < umbralEstimulo) continue;
      const g = grupoDe(m);
      if (!g) continue;
      const previo = ultimoPorGrupo.get(g);
      if (!previo || c.fecha > previo) ultimoPorGrupo.set(g, c.fecha);
    }
  }

  return GRUPOS.map((grupo) => {
    const s = series.get(grupo) ?? 0;
    const objetivo = objetivoSeriesSemanales[grupo] ?? null;
    const ultimo = ultimoPorGrupo.get(grupo) ?? null;
    const diasSin = ultimo ? diffDias(ultimo, hasta) : null;
    return {
      grupo,
      seriesEfectivas: s,
      seriesPrimarias: primariasEnRango.get(grupo) ?? 0,
      volumenKg: volumen.get(grupo) ?? 0,
      objetivo,
      cumplimiento: objetivo && objetivo > 0 ? s / objetivo : null,
      ultimoEstimulo: ultimo,
      diasSinEstimulo: diasSin,
      abandonado: diasSin === null || diasSin >= diasAbandono,
    };
  });
}

export function gruposAbandonados(
  ctxs: SerieContexto[],
  hasta: Fecha,
  diasAbandono = DIAS_ABANDONO,
): { grupo: GrupoMuscular; ultimoEstimulo: Fecha | null; diasSinEstimulo: number | null }[] {
  return mapaCalor(ctxs, hasta, { dias: diasAbandono, diasAbandono })
    .filter((c) => c.abandonado)
    .map((c) => ({ grupo: c.grupo, ultimoEstimulo: c.ultimoEstimulo, diasSinEstimulo: c.diasSinEstimulo }));
}

// ---------------------------------------------------------------------------
// Cuándo la ausencia de un músculo significa algo
// ---------------------------------------------------------------------------

/**
 * Sesiones mínimas para que "este grupo no aparece" sea un dato y no una obviedad.
 *
 * Con una sola sesión en el historial no hay una forma de entrenar: hay un día. Todo grupo que
 * ese día no tocó figura abandonado, y la app termina retando a alguien por no tener pasado —
 * justo la gamificación que castiga que prohíbe §10.
 */
export const MIN_SESIONES_PARA_ABANDONO = 2;

/**
 * Días mínimos de historial. Una rotación de 3 días necesita una semana para pasar por todos los
 * grupos: antes de eso, "pierna nunca" sólo dice que todavía no le tocó.
 *
 * Se piden los dos a la vez porque cada uno tapa el agujero del otro: quien entrenó 3 días
 * seguidos no completó una rotación, y quien registró 2 sesiones en tres semanas tampoco tiene
 * un patrón que juzgar.
 */
export const MIN_DIAS_PARA_ABANDONO = 7;

export interface HistorialAbandono {
  /** true cuando la ausencia de un grupo ya significa algo. */
  suficiente: boolean;
  /** Sesiones distintas con al menos una serie completada. */
  sesiones: number;
  /** Días corridos entre la primera serie registrada y `hasta`. 0 si no hay ninguna. */
  dias: number;
}

/**
 * Cuánto historial hay para juzgar una ausencia. Devuelve los números además del veredicto para
 * que la UI pueda decir por qué todavía no habla ("con 2 sesiones registradas no se puede decir
 * que un músculo esté abandonado"), en vez de callarse sin explicación.
 */
export function historialParaAbandono(ctxs: SerieContexto[], hasta: Fecha): HistorialAbandono {
  const efectivas = seriesEfectivas(ctxs).filter((c) => c.fecha <= hasta);
  const sesiones = new Set(efectivas.map((c) => c.sesion_id)).size;
  const primera = efectivas.reduce<Fecha | null>(
    (min, c) => (min === null || c.fecha < min ? c.fecha : min),
    null,
  );
  const dias = primera === null ? 0 : diffDias(primera, hasta);
  return {
    suficiente: sesiones >= MIN_SESIONES_PARA_ABANDONO && dias >= MIN_DIAS_PARA_ABANDONO,
    sesiones,
    dias,
  };
}

/** Ventana de 7 días que termina hoy, con el objetivo por grupo. Atajo para la home. */
export function resumenSemanal(
  ctxs: SerieContexto[],
  hoy: Fecha,
  objetivoSeriesSemanales: Partial<Record<GrupoMuscular, number>> = {},
): { celdas: CeldaMapaCalor[]; desde: Fecha; hasta: Fecha } {
  return {
    celdas: mapaCalor(ctxs, hoy, { dias: 7, objetivoSeriesSemanales }),
    desde: addDias(hoy, -6),
    hasta: hoy,
  };
}
